import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography, Box, Grid, Divider } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';

import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import {
  FBoxContainer,
  FHButton,
  FProgressBar,
  FHAutocomplete,
  FHInputField,
  FHTextarea,
} from '../../../base';
import MedicalHistoryRow from './MedicalHistoryRow';
import { medicalHistoryActions } from '../../../../store';
import { MedicalHistorySchema, initialMedicalHistory } from './validationSchema';
import {
  severityLevelOptions,
  errorStateConstants,
  MANDATORY_DIAGNOSIS,
} from './medicalHistoryConstants';
import {
  validateSchema,
  validateSync,
  getAllInvalidFieldNames,
} from '../../../../helpers/validation-helpers';
import { Themes, usStates } from '../../../../constants';
import { FORTA_WHITE, FORTA_BLUE, FORTA_ERROR } from '../../../../utils/colors';
import { debounce } from '../../../../helpers';
import { TreatmentPlanResourceVersion } from '../constants/general';
import ErrorIcon from '../../../../assets/images/error-alert.svg';

import type { IDiagnosis } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

function MedicalHistory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const editable = useTreatmentPlanEditable();

  const listOfKeys = [
    'autism_severity_level',
    'provider_name',
    'city_autism_diagnosis_made',
    'state_autism_diagnosis_made',
    'year_autism_diagnosis_made',
    'allergies',
    'special_diets',
    'trauma_history',
    'pregnancy_delivery_history',
    'hearing_vision_screening',
    'early_childhood_development',
    'early_childhood_intervention',
    'surgical_hospitalization_history',
  ];

  const { current: treatmentPlan, loading: treatmentPlanLoading } = useSelector(
    (state: any) => state.treatmentPlans,
  );
  const medicalHistoryVersion = treatmentPlan?.resource_versions?.medical_history;
  const isResourceVersionV2 = medicalHistoryVersion === TreatmentPlanResourceVersion.V2;
  const {
    medicalHistory,
    diagnoses,
    unsavedDiagnoses,
    unfinishedDiagnosis,
    loading: medicalHistoryLoading,
    triggerValidationsOnPageLoad,
    errorState,
  } = useSelector((state: any) => state.medicalHistories);
  const [errors, setErrors] = useState<any>({});

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      medicalHistoryActions.updateErrorState({ [errorStateConstants.medicalHistory]: errorCount }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);

  useEffect(() => {
    if (!treatmentPlan?.id) {
      return;
    }
    fetchMedicalHistory();
  }, [treatmentPlan]);

  useEffect(() => {
    if (!medicalHistory || Object.keys(medicalHistory).length === 0) {
      if (triggerValidationsOnPageLoad) {
        const initialData = {
          ...initialMedicalHistory,
          medical_history_version: medicalHistoryVersion,
        };
        const errors = getAllInvalidFieldNames(MedicalHistorySchema, initialData);
        setErrors(errors);
      }
      return;
    }
    listOfKeys.forEach((key: string) => {
      handleValidate(key, medicalHistory);
    });
  }, [medicalHistory]);

  useEffect(() => {
    debouncedErrorUpdater(errors);
  }, [errors]);

  useEffect(() => {
    if (!medicalHistory?.id) return;
    saveAllUnsavedDiagnoses();
  }, [medicalHistory]);

  const saveAllUnsavedDiagnoses = async () => {
    await dispatch(
      medicalHistoryActions.createMultipleDiagnoses({
        diagnoses: unsavedDiagnoses,
        getAccessTokenSilently,
        planId: treatmentPlan?.id,
        medicalHistoryId: medicalHistory?.id,
      }),
    );
    dispatch(medicalHistoryActions.updateState({ key: 'unsavedDiagnoses', data: [] }));
  };

  const fetchMedicalHistory = async () => {
    const { payload } = await dispatch(
      medicalHistoryActions.getMedicalHistory({ getAccessTokenSilently, planId: treatmentPlan.id }),
    );
    if (!payload?.id) return;
    dispatch(
      medicalHistoryActions.getDiagnoses({
        getAccessTokenSilently,
        planId: treatmentPlan?.id,
        medicalHistoryId: payload.id,
      }),
    );
  };

  const handleFieldUpdate = (field: string, value: string | number) => {
    const updatedData = { ...medicalHistory, [field]: value };
    dispatch(medicalHistoryActions.updateState({ key: 'medicalHistory', data: updatedData }));
  };

  const handleValidate = (key: string, data: any = medicalHistory) => {
    if (!data) return;

    const dataWithVersion = {
      ...data,
      medical_history_version: medicalHistoryVersion,
    };
    const isValid = validateSchema(MedicalHistorySchema, key, dataWithVersion);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleCreateOrUpdateMedicalHistory = async (field: string, value: string) => {
    const data = { ...medicalHistory, [field]: value };
    const dataWithVersion = {
      ...data,
      medical_history_version: medicalHistoryVersion,
    };
    const isValid = validateSync(MedicalHistorySchema, dataWithVersion);
    if (!isValid) return;

    let response;

    if (!medicalHistory.id) {
      response = await dispatch(
        medicalHistoryActions.createMedicalHistory({
          getAccessTokenSilently,
          planId: treatmentPlan?.id,
          data,
        }),
      );
    } else {
      response = await dispatch(
        medicalHistoryActions.updateMedicalHistory({
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          data,
        }),
      );
    }
    if ('error' in response && field === 'year_autism_diagnosis_made') {
      handleFieldUpdate('year_autism_diagnosis_made', '');
    }
  };

  const handleBlur = (key: string, value: string) => {
    handleValidate(key);
    handleCreateOrUpdateMedicalHistory(key, value);
  };

  const handleRemoveNewDiagnosis = () => {
    dispatch(medicalHistoryActions.updateState({ key: 'unfinishedDiagnosis', data: null }));
    dispatch(
      medicalHistoryActions.updateErrorState({
        [`${errorStateConstants.medicalHistoryRow}-new`]: 0,
      }),
    );
  };

  const MedicalHistoryTableHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Grid container spacing={2} mb={1} ml={2}>
        <Grid item xs={3}>
          <Typography fontWeight="bold">Diagnosis*</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography fontWeight="bold">Severity level*</Typography>
        </Grid>
        <Grid item xs={2.5}>
          <Typography fontWeight="bold">Diagnosis provider name*</Typography>
        </Grid>
        <Grid item xs={4.5}>
          <Typography fontWeight="bold">Diagnosis was made*</Typography>
        </Grid>
      </Grid>
    </Box>
  );

  if (!treatmentPlan?.id) {
    return <FBoxContainer colorTheme={Themes.Primary} title="Medical history" sx={{ mt: 3 }} />;
  }

  if (treatmentPlanLoading) {
    return <FProgressBar />;
  }

  return (
    <FBoxContainer colorTheme={Themes.Primary} title="Medical history" sx={{ mt: 3 }}>
      {editable && (
        <Typography
          ml={2}
          textAlign={'left'}
          alignSelf="start"
          fontWeight={'700'}
          lineHeight={'21px'}
          marginLeft={'0px'}
          marginBottom={3}
          sx={{
            color:
              errorState[errorStateConstants.medicalHistory] > 0 ||
              errorState[`${errorStateConstants.medicalHistoryRow}-new`] > 0
                ? FORTA_ERROR
                : FORTA_BLUE.light1,
          }}
        >
          {(errorState[errorStateConstants.medicalHistory] > 0 ||
            errorState[`${errorStateConstants.medicalHistoryRow}-new`] > 0) && (
            <img
              style={{ width: '20px', height: '20px', marginRight: '8px', marginTop: '-4px' }}
              src={ErrorIcon}
              alt="attention-icon"
            />
          )}
          Attention: All fields with * are required to be filled
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex' }} alignItems="flex-start">
            <Typography fontWeight={'bold'} textAlign="left" mr={2} mt={1}>
              Allergies:*
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <FHInputField
                data-testid="allergiesTextField"
                value={medicalHistory?.allergies ?? ''}
                inputHeight={40}
                fullWidth
                disabled={!editable}
                colorTheme={Themes.Primary}
                onChange={(e) => handleFieldUpdate('allergies', e.target.value)}
                onBlur={(e) => handleBlur('allergies', e.target.value)}
                error={errors['allergies']}
              />
              <Typography textAlign="left" mt={1} ml={1}>
                If no allergies are present, please write &quot;none&quot;.
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex' }} alignItems="flex-start">
            <Typography
              style={{ width: '140px' }}
              fontWeight={'bold'}
              textAlign="right"
              mr={2}
              mt={1}
            >
              Special diets:*
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <FHInputField
                data-testid="specialdietsTextField"
                inputHeight={40}
                value={medicalHistory?.special_diets ?? ''}
                fullWidth
                disabled={!editable}
                colorTheme={Themes.Primary}
                onChange={(e) => handleFieldUpdate('special_diets', e.target.value)}
                onBlur={(e) => handleBlur('special_diets', e.target.value)}
                error={errors['special_diets']}
              />
              <Typography textAlign="left" mt={1} ml={1}>
                If no special diets are present, please write &quot;none&quot;.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Box>
        <Divider
          sx={{ height: 3, backgroundColor: FORTA_BLUE.primary, my: 3, borderRadius: '3px' }}
        />
      </Box>
      <MedicalHistoryTableHeader />
      <Box>
        <Grid container spacing={2} pl={4.2}>
          <Grid item xs={5}>
            <Grid container>
              <Grid item xs={7.5}>
                <Typography sx={{ flexGrow: 1, mt: 1 }}>{MANDATORY_DIAGNOSIS}</Typography>
              </Grid>
              <Grid item xs={4.5}>
                <FHAutocomplete
                  data-testid="autismSeverityLevelDropDown"
                  value={medicalHistory?.autism_severity_level ?? ''}
                  options={severityLevelOptions}
                  placeholder="Select"
                  onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: string) => {
                    handleFieldUpdate('autism_severity_level', newValue);
                    handleCreateOrUpdateMedicalHistory('autism_severity_level', newValue);
                  }}
                  isOptionEqualToValue={() => true}
                  colorTheme={Themes.Primary}
                  disabled={!editable}
                  minWidth={50}
                  inputHeight={40}
                  error={errors['autism_severity_level']}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={2.5}>
            <FHInputField
              data-testid="diagnosisProviderNameTextField"
              value={medicalHistory?.provider_name ?? ''}
              fullWidth
              inputHeight={40}
              disabled={!editable}
              colorTheme={Themes.Primary}
              onChange={(e) => handleFieldUpdate('provider_name', e.target.value)}
              onBlur={(e) => handleBlur('provider_name', e.target.value)}
              error={errors['provider_name']}
            />
          </Grid>
          <Grid item xs={4.5}>
            <Grid container spacing={2}>
              <Grid item xs={5.5}>
                <FHInputField
                  data-testid="cityDiagnosisIsMadeTextField"
                  value={medicalHistory?.city_autism_diagnosis_made ?? ''}
                  fullWidth
                  inputHeight={40}
                  disabled={!editable}
                  colorTheme={Themes.Primary}
                  onChange={(e) => handleFieldUpdate('city_autism_diagnosis_made', e.target.value)}
                  onBlur={(e) => handleBlur('city_autism_diagnosis_made', e.target.value)}
                  placeholder={'City'}
                  sx={{ minWidth: 50 }}
                  error={errors['city_autism_diagnosis_made']}
                />
              </Grid>
              <Grid item xs={3.5}>
                <FHAutocomplete
                  data-testid="stateDiagnosIsMadeDropDown"
                  value={medicalHistory?.state_autism_diagnosis_made ?? ''}
                  options={usStates}
                  placeholder={'State'}
                  onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: string) => {
                    handleFieldUpdate('state_autism_diagnosis_made', newValue);
                    handleCreateOrUpdateMedicalHistory('state_autism_diagnosis_made', newValue);
                  }}
                  isOptionEqualToValue={() => true}
                  colorTheme={Themes.Primary}
                  disabled={!editable}
                  disableClearable
                  minWidth={50}
                  inputHeight={40}
                  error={errors['state_autism_diagnosis_made']}
                />
              </Grid>
              <Grid item xs={3}>
                <FHInputField
                  data-testid="yearDiagnosIsMadeDropDown"
                  type="number"
                  value={medicalHistory?.year_autism_diagnosis_made ?? ''}
                  fullWidth
                  disabled={!editable}
                  colorTheme={Themes.Primary}
                  onChange={(e) => handleFieldUpdate('year_autism_diagnosis_made', e.target.value)}
                  onBlur={(e) => handleBlur('year_autism_diagnosis_made', e.target.value)}
                  placeholder={'Year'}
                  sx={{ minWidth: 50 }}
                  inputHeight={40}
                  error={errors['year_autism_diagnosis_made']}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {diagnoses.map((diagnosis: IDiagnosis) => (
        <MedicalHistoryRow
          diagnosis={diagnosis}
          key={diagnosis.id}
          medicalHistoryId={medicalHistory?.id}
        />
      ))}
      {unsavedDiagnoses.map((diagnosis: IDiagnosis) => (
        <MedicalHistoryRow
          diagnosis={diagnosis}
          key={diagnosis.id}
          medicalHistoryId={medicalHistory?.id}
        />
      ))}
      {Boolean(unfinishedDiagnosis) && (
        <MedicalHistoryRow
          nextIndex={
            unsavedDiagnoses.length > 0 ? unsavedDiagnoses[unsavedDiagnoses.length - 1].id + 1 : 1
          }
          medicalHistoryId={medicalHistory?.id}
          onRemove={handleRemoveNewDiagnosis}
        />
      )}
      {editable && (
        <Box sx={{ display: 'flex', mt: 2 }}>
          <FHButton
            data-testid="addDiagnosisButton"
            disabled={Boolean(unfinishedDiagnosis) || !medicalHistory?.id}
            colorTheme={Themes.Primary}
            onClick={() =>
              dispatch(medicalHistoryActions.updateState({ key: 'unfinishedDiagnosis', data: {} }))
            }
            startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
          >
            Add Diagnosis
          </FHButton>
        </Box>
      )}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={isResourceVersionV2 ? 6 : 12}>
          <Box sx={{ display: 'flex-column' }} alignItems="flex-start">
            <Typography fontWeight={'bold'} textAlign="left" mb={1}>
              Trauma history:*
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <FHTextarea
                data-testid="traumaHistoryTextField"
                value={medicalHistory?.trauma_history ?? ''}
                maxRows={3}
                fullWidth
                disabled={!editable}
                colorTheme={Themes.Primary}
                onEdit={(e) => handleFieldUpdate('trauma_history', e.target.value)}
                onBlur={(e) => handleBlur('trauma_history', e.target.value)}
                error={editable && errors['trauma_history']}
              />
            </Box>
          </Box>
        </Grid>
        {isResourceVersionV2 && (
          <>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex-column' }} alignItems="flex-start">
                <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                  Pregnancy and Delivery History:*
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <FHTextarea
                    data-testid="pregnancyHistoryTextField"
                    value={medicalHistory?.pregnancy_delivery_history ?? ''}
                    maxRows={3}
                    fullWidth
                    disabled={!editable}
                    colorTheme={Themes.Primary}
                    onEdit={(e) => handleFieldUpdate('pregnancy_delivery_history', e.target.value)}
                    onBlur={(e) => handleBlur('pregnancy_delivery_history', e.target.value)}
                    error={editable && errors['pregnancy_delivery_history']}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex-column' }} alignItems="flex-start">
                <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                  Hearing and Vision Screening:*
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <FHTextarea
                    data-testid="hearingVisionScreeningTextField"
                    value={medicalHistory?.hearing_vision_screening ?? ''}
                    maxRows={3}
                    fullWidth
                    disabled={!editable}
                    colorTheme={Themes.Primary}
                    onEdit={(e) => handleFieldUpdate('hearing_vision_screening', e.target.value)}
                    onBlur={(e) => handleBlur('hearing_vision_screening', e.target.value)}
                    error={editable && errors['hearing_vision_screening']}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex-column' }} alignItems="flex-start">
                <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                  Early Childhood Development:*
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <FHTextarea
                    data-testid="earlyChildhoodDevelopmentTextField"
                    value={medicalHistory?.early_childhood_development ?? ''}
                    maxRows={3}
                    fullWidth
                    disabled={!editable}
                    colorTheme={Themes.Primary}
                    onEdit={(e) => handleFieldUpdate('early_childhood_development', e.target.value)}
                    onBlur={(e) => handleBlur('early_childhood_development', e.target.value)}
                    error={editable && errors['early_childhood_development']}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex-column' }} alignItems="flex-start">
                <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                  Early Childhood Intervention (ECI):*
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <FHTextarea
                    data-testid="earlyChildhoodInterventionTextField"
                    value={medicalHistory?.early_childhood_intervention ?? ''}
                    maxRows={3}
                    fullWidth
                    disabled={!editable}
                    colorTheme={Themes.Primary}
                    onEdit={(e) =>
                      handleFieldUpdate('early_childhood_intervention', e.target.value)
                    }
                    onBlur={(e) => handleBlur('early_childhood_intervention', e.target.value)}
                    error={editable && errors['early_childhood_intervention']}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex-column' }} alignItems="flex-start">
                <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                  Surgical and/or Hospitalization History:*
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <FHTextarea
                    data-testid="surgicalHospitalizationHistoryTextField"
                    value={medicalHistory?.surgical_hospitalization_history ?? ''}
                    maxRows={3}
                    fullWidth
                    disabled={!editable}
                    colorTheme={Themes.Primary}
                    onEdit={(e) =>
                      handleFieldUpdate('surgical_hospitalization_history', e.target.value)
                    }
                    onBlur={(e) => handleBlur('surgical_hospitalization_history', e.target.value)}
                    error={editable && errors['surgical_hospitalization_history']}
                  />
                </Box>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      {medicalHistoryLoading && <FProgressBar />}
    </FBoxContainer>
  );
}

export default MedicalHistory;
